import * as React from 'react';

/**
 * Returns focus to whatever was focused before an overlay opened.
 *
 * Radix restores focus to its Trigger, but most dialogs here are controlled —
 * opened from row buttons or imperative flows with no Trigger — and in that
 * case Radix falls back to <body>, dropping a keyboard user at the top of the
 * document. The capture runs in a layout effect on the overlay content's
 * mount, which is before Radix moves focus into the overlay.
 */
export function useReturnFocus(consumerHandler?: (event: Event) => void) {
  const openerRef = React.useRef<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }, []);

  return React.useCallback(
    (event: Event) => {
      consumerHandler?.(event);
      if (event.defaultPrevented) return;
      const opener = openerRef.current;
      if (opener && opener.isConnected) {
        event.preventDefault();
        opener.focus();
      }
    },
    [consumerHandler],
  );
}
